interface InstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface InstallBannerConfig {
  minVisits: number;
  minTimeOnSite: number; // in seconds
  cooldownPeriod: number; // in days
}

interface PlatformInfo {
  isIOS: boolean;
  isAndroid: boolean;
  isChrome: boolean;
  isEdge: boolean;
  isSafari: boolean;
  isFirefox: boolean;
  isMobile: boolean;
  isStandalone: boolean;
  supportsInstallPrompt: boolean;
}

interface UserInstallPreferences {
  dismissed: boolean;
  dismissedAt: number;
  installPromptShown: number;
  lastShownAt: number;
}

class PWAInstallService {
  private static instance: PWAInstallService;
  private installPromptEvent: InstallPromptEvent | null = null;
  private platformInfo: PlatformInfo;
  private config: InstallBannerConfig = {
    minVisits: 2,
    minTimeOnSite: 30, // 30 seconds
    cooldownPeriod: 7 // 7 days
  };

  private constructor() {
    this.platformInfo = this.detectPlatform();
    this.initializeInstallPrompt();
  }

  public static getInstance(): PWAInstallService {
    if (!PWAInstallService.instance) {
      PWAInstallService.instance = new PWAInstallService();
    }
    return PWAInstallService.instance;
  }

  private detectPlatform(): PlatformInfo {
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isChrome = /chrome/.test(userAgent) && !/edg/.test(userAgent);
    const isEdge = /edg/.test(userAgent);
    const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);
    const isFirefox = /firefox/.test(userAgent);
    const isMobile = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    
    // Check if app is already running in standalone mode
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                        (window.navigator as any).standalone === true;

    // Native install prompt support (Chrome/Edge on Android/Desktop)
    const supportsInstallPrompt = (isChrome || isEdge) && !isIOS;

    return {
      isIOS,
      isAndroid,
      isChrome,
      isEdge,
      isSafari,
      isFirefox,
      isMobile,
      isStandalone,
      supportsInstallPrompt
    };
  }

  private initializeInstallPrompt(): void {
    console.log('📱 Initializing install prompt, platform info:', this.platformInfo);
    
    if (!this.platformInfo.supportsInstallPrompt) {
      console.log('📱 Platform does not support install prompts');
      return;
    }

    // Add event listener for install prompt
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      console.log('📱 🎉 Install prompt event captured successfully!');
      e.preventDefault();
      this.installPromptEvent = e as InstallPromptEvent;
      
      // Dispatch custom event to update UI
      window.dispatchEvent(new CustomEvent('pwa-install-available'));
    });

    // Listen for successful app installation
    window.addEventListener('appinstalled', () => {
      console.log('📱 ✅ App successfully installed');
      this.recordInstallation();
      this.installPromptEvent = null; // Clear the prompt
      
      // Dispatch custom event to update UI
      window.dispatchEvent(new CustomEvent('pwa-installed'));
    });

    // Check if install prompt might be available later
    setTimeout(() => {
      if (!this.installPromptEvent && this.platformInfo.supportsInstallPrompt) {
        console.log('📱 ⚠️ Install prompt not captured yet. This could mean:');
        console.log('- App is already installed');
        console.log('- PWA criteria not met (check manifest, service worker, HTTPS)');
        console.log('- Browser needs more user engagement');
        console.log('- Try refreshing or navigating to different pages');
      }
    }, 3000);
  }

  public getPlatformInfo(): PlatformInfo {
    return { ...this.platformInfo };
  }

  public isAppInstallable(): boolean {
    if (this.platformInfo.isStandalone) {
      return false; // Already installed
    }

    return this.platformInfo.supportsInstallPrompt || 
           this.platformInfo.isIOS || 
           this.platformInfo.isAndroid;
  }

  public getInstallMethod(): 'native' | 'ios-safari' | 'manual' | 'none' {
    if (!this.isAppInstallable()) {
      return 'none';
    }

    if (this.installPromptEvent && this.platformInfo.supportsInstallPrompt) {
      return 'native';
    }

    if (this.platformInfo.isIOS && this.platformInfo.isSafari) {
      return 'ios-safari';
    }

    return 'manual';
  }

  public async triggerInstallPrompt(): Promise<boolean> {
    console.log('📱 Trigger install prompt called');
    console.log('📱 Install prompt event available:', !!this.installPromptEvent);
    console.log('📱 Platform supports install:', this.platformInfo.supportsInstallPrompt);
    console.log('📱 Current install method:', this.getInstallMethod());

    // If we have a native prompt, use it
    if (this.installPromptEvent) {
      try {
        console.log('📱 Triggering native install prompt...');
        await this.installPromptEvent.prompt();
        const choiceResult = await this.installPromptEvent.userChoice;
        
        console.log('📱 Install prompt result:', choiceResult.outcome);
        
        if (choiceResult.outcome === 'accepted') {
          this.recordInstallation();
          return true;
        }
        
        return false;
      } catch (error) {
        console.error('📱 Install prompt failed:', error);
        return false;
      }
    }

    // For iOS Safari or when native prompt isn't available, show instructions
    if (this.platformInfo.isIOS && this.platformInfo.isSafari) {
      console.log('📱 iOS Safari detected - showing instructions modal');
      this.showInstallInstructionsModal();
      return true; // Return true as we showed instructions
    }

    // For other browsers without native prompt
    if (this.platformInfo.supportsInstallPrompt) {
      console.log('📱 ⚠️ Browser supports install prompts but prompt not available');
      console.log('📱 This might happen if:');
      console.log('- PWA installability criteria not met');
      console.log('- App already installed');
      console.log('- Need more user engagement');
      console.log('- Service worker or manifest issues');
      
      // Show manual instructions
      this.showInstallInstructionsModal();
      return true;
    }

    console.log('📱 No install method available for this platform');
    return false;
  }

  public shouldShowInstallBanner(): boolean {
    if (!this.isAppInstallable()) {
      return false;
    }

    const preferences = this.getUserPreferences();
    
    // Check if user permanently dismissed
    if (preferences.dismissed && 
        Date.now() - preferences.dismissedAt < this.config.cooldownPeriod * 24 * 60 * 60 * 1000) {
      return false;
    }

    // Check minimum engagement requirements
    const visits = this.getVisitCount();
    const timeOnSite = this.getTimeOnSite();

    return visits >= this.config.minVisits && timeOnSite >= this.config.minTimeOnSite;
  }

  public recordBannerShown(): void {
    const preferences = this.getUserPreferences();
    preferences.installPromptShown += 1;
    preferences.lastShownAt = Date.now();
    this.saveUserPreferences(preferences);
  }

  public recordBannerDismissed(permanent: boolean = false): void {
    const preferences = this.getUserPreferences();
    
    if (permanent) {
      preferences.dismissed = true;
      preferences.dismissedAt = Date.now();
    }
    
    this.saveUserPreferences(preferences);
  }

  private recordInstallation(): void {
    localStorage.setItem('pwa_installed', 'true');
    localStorage.setItem('pwa_installed_at', Date.now().toString());
  }

  private getUserPreferences(): UserInstallPreferences {
    const stored = localStorage.getItem('pwa_install_preferences');
    
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.warn('Failed to parse install preferences:', error);
      }
    }

    return {
      dismissed: false,
      dismissedAt: 0,
      installPromptShown: 0,
      lastShownAt: 0
    };
  }

  private saveUserPreferences(preferences: UserInstallPreferences): void {
    localStorage.setItem('pwa_install_preferences', JSON.stringify(preferences));
  }

  private getVisitCount(): number {
    const visits = localStorage.getItem('visit_count');
    if (visits) {
      return parseInt(visits, 10);
    }

    // Initialize visit tracking
    const currentCount = 1;
    localStorage.setItem('visit_count', currentCount.toString());
    return currentCount;
  }

  private incrementVisitCount(): void {
    const currentCount = this.getVisitCount();
    localStorage.setItem('visit_count', (currentCount + 1).toString());
  }

  private getTimeOnSite(): number {
    const sessionStart = sessionStorage.getItem('session_start');
    
    if (!sessionStart) {
      sessionStorage.setItem('session_start', Date.now().toString());
      return 0;
    }

    return Math.floor((Date.now() - parseInt(sessionStart, 10)) / 1000);
  }

  public initializeSession(): void {
    // Track page visit
    this.incrementVisitCount();
    
    // Initialize session timing
    if (!sessionStorage.getItem('session_start')) {
      sessionStorage.setItem('session_start', Date.now().toString());
    }
  }

  public getInstallInstructions(): {
    title: string;
    steps: string[];
    icon: string;
  } {
    const platform = this.getPlatformInfo();

    if (platform.isIOS && platform.isSafari) {
      return {
        title: 'Add to Home Screen',
        steps: [
          'Tap the Share button below',
          'Scroll down and tap "Add to Home Screen"',
          'Tap "Add" to confirm'
        ],
        icon: '📲'
      };
    }

    if (platform.isAndroid && platform.isChrome) {
      return {
        title: 'Install App',
        steps: [
          'Tap the menu (⋮) in your browser',
          'Select "Add to Home screen" or "Install app"',
          'Tap "Install" to confirm'
        ],
        icon: '📱'
      };
    }

    if (platform.isChrome || platform.isEdge) {
      return {
        title: 'Install App',
        steps: [
          'Click the install icon in your address bar',
          'Or use the browser menu → "Install T1 FX Team"',
          'Click "Install" to confirm'
        ],
        icon: '💻'
      };
    }

    return {
      title: 'Add to Home Screen',
      steps: [
        'Use your browser\'s menu',
        'Look for "Add to Home Screen" or "Install"',
        'Follow the prompts to install'
      ],
      icon: '📱'
    };
  }

  private showInstallInstructionsModal(): void {
    // Create a modal with install instructions
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10000;
      backdrop-filter: blur(10px);
    `;

    const instructions = this.getInstallInstructions();
    
    modal.innerHTML = `
      <div style="
        background: linear-gradient(135deg, #0A0E14 0%, #1a1f2e 100%);
        border: 2px solid #FFD700;
        border-radius: 16px;
        padding: 32px;
        max-width: 90%;
        max-height: 90%;
        width: 400px;
        color: white;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        box-shadow: 0 12px 48px rgba(0, 0, 0, 0.5);
      ">
        <div style="text-align: center; margin-bottom: 24px;">
          <div style="font-size: 48px; margin-bottom: 16px;">${instructions.icon}</div>
          <h2 style="margin: 0; color: #FFD700; font-size: 24px;">${instructions.title}</h2>
        </div>
        
        <div style="margin-bottom: 24px;">
          ${instructions.steps.map((step, index) => `
            <div style="
              display: flex;
              align-items: flex-start;
              margin-bottom: 12px;
              padding: 8px;
              background: rgba(255, 215, 0, 0.1);
              border-radius: 8px;
            ">
              <div style="
                background: #FFD700;
                color: #000;
                border-radius: 50%;
                width: 24px;
                height: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                font-size: 14px;
                margin-right: 12px;
                flex-shrink: 0;
              ">${index + 1}</div>
              <div style="font-size: 16px; line-height: 1.4;">${step}</div>
            </div>
          `).join('')}
        </div>

        ${this.platformInfo.isIOS ? `
          <div style="
            background: rgba(255, 215, 0, 0.1);
            border: 1px solid rgba(255, 215, 0, 0.3);
            border-radius: 8px;
            padding: 12px;
            margin-bottom: 24px;
            font-size: 14px;
          ">
            💡 <strong>Tip:</strong> Look for the Share button (□↗) at the bottom of your Safari screen
          </div>
        ` : ''}

        <div style="text-align: center;">
          <button id="install-modal-close" style="
            background: #FFD700;
            color: #000;
            border: none;
            padding: 12px 32px;
            border-radius: 8px;
            font-weight: bold;
            font-size: 16px;
            cursor: pointer;
            transition: all 0.2s ease;
          " onmouseover="this.style.background='#E6C200'" onmouseout="this.style.background='#FFD700'">
            Got it!
          </button>
        </div>
      </div>
    `;

    // Add click handler to close modal
    modal.addEventListener('click', (e) => {
      if (e.target === modal || (e.target as HTMLElement).id === 'install-modal-close') {
        document.body.removeChild(modal);
      }
    });

    // Add to page
    document.body.appendChild(modal);

    // Record that instructions were shown
    this.recordBannerShown();
  }

  public getInstallStatus(): {
    hasInstallPrompt: boolean;
    isInstallable: boolean;
    isStandalone: boolean;
    installMethod: string;
    platformInfo: PlatformInfo;
    debugInfo: any;
  } {
    return {
      hasInstallPrompt: !!this.installPromptEvent,
      isInstallable: this.isAppInstallable(),
      isStandalone: this.platformInfo.isStandalone,
      installMethod: this.getInstallMethod(),
      platformInfo: this.platformInfo,
      debugInfo: {
        userAgent: navigator.userAgent,
        isHTTPS: window.location.protocol === 'https:',
        hasServiceWorker: 'serviceWorker' in navigator,
        hasManifest: document.querySelector('link[rel="manifest"]') !== null,
        visitCount: this.getVisitCount(),
        timeOnSite: this.getTimeOnSite(),
      }
    };
  }

  public reset(): void {
    localStorage.removeItem('pwa_install_preferences');
    localStorage.removeItem('visit_count');
    localStorage.removeItem('pwa_installed');
    localStorage.removeItem('pwa_installed_at');
    sessionStorage.removeItem('session_start');
  }
}

export default PWAInstallService; 